import { useCallback, useEffect, useState } from "react";
import { Headphones, LoaderCircle } from "lucide-react";
import { AuthScreen } from "./AuthScreen";
import { SoundCloudApp } from "./SoundCloudApp";
import { api, ApiError } from "../lib/api";
import type { Bootstrap } from "../types";

type Phase = "checking" | "locked" | "ready" | "error";

export function SoundCloudStandalone() {
  const [phase, setPhase] = useState<Phase>("checking");
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null);
  const [message, setMessage] = useState("Opening your private music library…");

  const open = useCallback(async () => {
    setPhase("checking");
    setMessage("Opening your private music library…");
    try {
      await api.soundcloudLibrary();
      setPhase("ready");
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        try {
          const value = await api.bootstrap();
          setBootstrap(value);
          setPhase("locked");
        } catch {
          setMessage("The music server is unavailable.");
          setPhase("error");
        }
        return;
      }
      setMessage(error instanceof Error ? error.message : "The music library is unavailable.");
      setPhase("error");
    }
  }, []);

  useEffect(() => { void open(); }, [open]);
  useEffect(() => {
    const previous = document.title;
    document.title = "SoundCloud Player · NKriZ";
    return () => { document.title = previous; };
  }, []);

  if (phase === "ready") return <SoundCloudApp />;
  if (phase === "locked" && bootstrap) {
    return <AuthScreen bootstrap={bootstrap} notice="Unlock to open the standalone SoundCloud player." variant="soundcloud" onUnlocked={() => setPhase("ready")} />;
  }
  return <main className="soundcloud-standalone-state">
    <span><Headphones size={30} /></span>
    {phase === "checking" && <LoaderCircle className="spin" size={22} />}
    <strong>{phase === "error" ? "SoundCloud player unavailable" : "SoundCloud Player"}</strong>
    <p>{message}</p>
    {phase === "error" && <button onClick={() => void open()}>Try again</button>}
  </main>;
}
